import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OnboardingApiError } from "../../OnboardingAPI";
import { cn } from "@/lib/utils";
import { ButtonTip } from "@/components/ui/button-tip";
import { Collapse, CollapsePresence, usePrefersReducedMotion } from "../calm";
import { Field } from "../Fields";
import { StepBody, StepFooter, StepHeading } from "../StepCard";
import { numberedCount } from "../steps";
import type { StepContext } from "./shared";

/** The code the server sends has this many digits. */
export const CODE_LENGTH = 6;

/** Seconds until `at` (ISO), 0 when it is past or unknown: the server's clock time decides, not a number kept here. */
export const secondsUntil = (at: string | undefined, now: number): number => {
  const time = at ? Date.parse(at) : NaN;
  return Number.isFinite(time) ? Math.max(0, Math.ceil((time - now) / 1000)) : 0;
};

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** Each part of the step comes in a little after the one before. */
const STAGGER_MS = 80;
/** How long the field shakes after a wrong code. */
const SHAKE_MS = 300;
/** The fade between the code field and the "confirmed" mark. */
const SWAP_MS = 320;

/** A part of the step that fades in (and lifts a little) after `index` steps of the stagger; without the delay under reduced motion. */
function Enter({ index, children, className }: { index: number; children: ReactNode; className?: string }) {
  const reduced = usePrefersReducedMotion();
  const style = { "--onb-delay": `${reduced ? 0 : index * STAGGER_MS}ms` } as CSSProperties;
  return (
    <div className={cn("onb-enter", className)} style={style} data-enter={index}>
      {children}
    </div>
  );
}

/** One of two things that take the same place: the other fades out while this fades in, and nothing around them moves. */
function Layer({ show, children, testId }: { show: boolean; children: ReactNode; testId?: string }) {
  const style: CSSProperties = {
    gridArea: "1 / 1",
    opacity: show ? 1 : 0,
    visibility: show ? "visible" : "hidden",
    transition: `opacity ${SWAP_MS}ms cubic-bezier(0.22, 1, 0.36, 1), visibility 0s linear ${show ? 0 : SWAP_MS}ms`,
  };
  return (
    <div style={style} aria-hidden={show ? undefined : true} data-testid={testId}>
      {children}
    </div>
  );
}

/**
 * "Fast fertig": the number the person gave is confirmed by a code sent by SMS before anything is sent. Everything
 * stands in the middle of the card and comes in one part after the other. The code goes out once on arrival (if there
 * is none yet and the server allows a send), a new one can be asked for when the server's `nextSendAt` has come (a
 * countdown made from it, so it survives a reload), six digits are checked as soon as they are all there, and the
 * number can be changed on the spot (the number gives way to a field, and back). A wrong code says nothing in words:
 * the field shakes (a short change of colour under reduced motion), empties and keeps the focus; a screen reader gets
 * one quiet sentence. An expired code, or too many tries, gets a calm hint and a highlighted "new code" button -
 * no red, no numbers.
 */
export default function PhoneStep({ ctx }: { ctx: StepContext }) {
  const { c } = ctx;
  const { t } = useTranslation();
  const phone = c.view!.phone!;
  const verified = phone.verified;

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [changing, setChanging] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const [announce, setAnnounce] = useState("");
  const codeInput = useRef<HTMLInputElement>(null);
  const newInput = useRef<HTMLInputElement>(null);
  const autoSent = useRef(false);
  const lastTried = useRef("");
  const timers = useRef<number[]>([]);
  const later = (run: () => void, ms: number) => {
    timers.current.push(window.setTimeout(run, ms));
  };
  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), []);

  const left = secondsUntil(phone.nextSendAt, now);
  const waiting = left > 0;

  // the countdown: re-read from the server's time each second, only while there is something to count
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [waiting]);
  // when it ends, say so to a screen reader (the ticking itself is not announced)
  const wasWaiting = useRef(false);
  useEffect(() => {
    if (wasWaiting.current && !waiting) setAnnounce(t("onboardingPage.phone.canResend"));
    wasWaiting.current = waiting;
  }, [waiting, t]);

  /** A sentence for a screen reader only; clearing first makes the same sentence read out again. */
  const say = (text: string) => {
    setAnnounce("");
    later(() => setAnnounce(text), 60);
  };

  const shake = () => {
    setShaking(false);
    later(() => setShaking(true), 20);
    later(() => setShaking(false), 20 + SHAKE_MS + 20);
  };

  const errorText = (caught: unknown): string => {
    if (!(caught instanceof OnboardingApiError)) return t("onboardingPage.phone.errors.unknown");
    const own = (key: string) => t(`onboardingPage.phone.errors.${key}`);
    // a real sentence of the server (the daily limit, the SMS that failed) is better than ours
    const serverSentence = caught.message && caught.message !== caught.code ? caught.message : null;
    switch (caught.code) {
      case "rate_limited":
        return serverSentence ?? own("rate_limited");
      case "sms_failed":
        return serverSentence ?? own("sms_failed");
      case "invalid_request":
        return serverSentence ?? own("invalid_request");
      default:
        return own("unknown");
    }
  };

  const send = async (newPhone?: string) => {
    if (sending) return;
    setSending(true);
    setError(null);
    setNotice(null);
    setCode("");
    lastTried.current = "";
    try {
      await c.sendPhoneCode(newPhone);
      // the screen reader follows the state: a new code is out (and the countdown runs), not "you can send a new code"
      say(t("onboardingPage.phone.codeSent"));
      setChanging(false);
      setNewNumber("");
      setNow(Date.now());
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSending(false);
    }
  };

  // on arrival, once: a code goes out if there is none yet and the server does not make us wait
  useEffect(() => {
    if (autoSent.current) return;
    autoSent.current = true;
    if (verified || phone.codeSent || secondsUntil(phone.nextSendAt, Date.now()) > 0) return;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // the focus goes where the next thing is typed: the code, or the new number while it is being changed
  const first = useRef(true);
  useEffect(() => {
    if (verified) return;
    const target = changing ? newInput : codeInput;
    if (first.current) {
      first.current = false;
      if (!changing) target.current?.focus();
      return;
    }
    const frame = requestAnimationFrame(() => target.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [verified, changing]);

  const check = async (digits: string) => {
    if (checking || digits === lastTried.current) return;
    lastTried.current = digits;
    setChecking(true);
    setError(null);
    setNotice(null);
    try {
      await c.verifyPhoneCode(digits);
    } catch (caught) {
      setCode("");
      lastTried.current = "";
      const failure = caught instanceof OnboardingApiError ? caught.code : "";
      if (failure === "code_expired" || failure === "too_many_attempts") {
        // a calm hint and a highlighted button, nothing red, no numbers
        setNotice(t("onboardingPage.phone.renewCode"));
        say(t("onboardingPage.phone.renewCode"));
      } else if (failure === "invalid_code") {
        shake();
        say(t("onboardingPage.phone.codeRejected"));
      } else {
        setError(errorText(caught));
      }
    } finally {
      setChecking(false);
    }
  };

  const onCode = (raw: string) => {
    // digits only; a pasted "123 456" arrives as it is and leaves as 123456
    const digits = raw.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    if (digits.length === CODE_LENGTH) void check(digits);
  };

  const progress = (sending || checking) && (
    <p role="status" className="flex items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {checking ? t("onboardingPage.phone.checking") : t("onboardingPage.phone.sending")}
    </p>
  );
  const calm = (text: string | null, id: string, testId: string) =>
    text && (
      <p id={id} role="status" className="text-muted-foreground" data-testid={testId}>
        {text}
      </p>
    );

  return (
    <>
      <StepBody className="flex flex-col items-center justify-center text-center">
        <div className="w-full max-w-[26rem] space-y-6" data-keys-own data-testid="phone-step">
          <Enter index={0}>
            <StepHeading
              centered
              eyebrow={t("onboardingPage.nav.stepOf", { current: ctx.step.index, total: numberedCount(ctx.steps) })}
              title={t("onboardingPage.titles.phone")}
              lead={verified ? undefined : t("onboardingPage.phone.lead")}
              focus={ctx.focus}
            />
          </Enter>
          {ctx.nudge && (
            <p role="status" className="rounded-xl bg-muted/60 px-4 py-3 text-sm">{ctx.nudge}</p>
          )}

          <Enter index={1}>
            {/* the number, or (while it is being changed) a field for a new one: height and opacity move together */}
            <Collapse open={!changing}>
              {phone.number && (
                <p className="py-1 text-[22px] font-semibold leading-8 tabular-nums sm:text-2xl" data-testid="phone-number">
                  {phone.number}
                </p>
              )}
            </Collapse>
            <CollapsePresence show={changing}>
              <div className="space-y-3 px-1 py-1 text-left" data-testid="phone-change-form">
                <Field id="onboarding-phone-new" label={t("onboardingPage.phone.newNumberLabel")}>
                  <Input
                    id="onboarding-phone-new"
                    ref={newInput}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={newNumber}
                    maxLength={40}
                    onChange={(event) => setNewNumber(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && newNumber.trim() && !waiting) {
                        event.preventDefault();
                        void send(newNumber.trim());
                      }
                    }}
                  />
                </Field>
                <div className="min-h-5 text-center text-sm">
                  {progress}
                  {calm(error, "onboarding-phone-error", "phone-error")}
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <ButtonTip label={waiting ? t("onboardingPage.tips.resendWait", { time: clock(left) }) : !newNumber.trim() ? t("onboardingPage.tips.enterNumber") : t("onboardingPage.tips.sendNew")} disabled={!newNumber.trim() || sending || waiting}>
                  <Button type="button" disabled={!newNumber.trim() || sending || waiting} onClick={() => void send(newNumber.trim())} data-testid="phone-send-new">
                    {waiting ? t("onboardingPage.phone.resendIn", { time: clock(left) }) : t("onboardingPage.phone.sendNew")}
                  </Button>
                  </ButtonTip>
                  <ButtonTip label={t("onboardingPage.tips.cancelChange")}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setChanging(false);
                      setNewNumber("");
                      setError(null);
                    }}
                  >
                    {t("onboardingPage.phone.cancel")}
                  </Button>
                  </ButtonTip>
                </div>
              </div>
            </CollapsePresence>
          </Enter>

          <Enter index={2}>
            {/* the code field and its buttons - or, once the number is confirmed, the mark that takes their place (same room, nothing jumps) */}
            <div className="grid">
              <Layer show={!verified} testId="phone-code-layer">
                <Collapse open={!changing}>
                  <div className="space-y-4 px-1 py-1">
                    <Field id="onboarding-phone-code" label={t("onboardingPage.phone.codeLabel")}>
                      <Input
                        id="onboarding-phone-code"
                        ref={codeInput}
                        value={code}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={CODE_LENGTH + 4}
                        readOnly={checking}
                        placeholder="000000"
                        onChange={(event) => onCode(event.target.value)}
                        className={cn("mx-auto h-14 max-w-[14rem] text-center font-mono text-3xl tracking-[0.35em] tabular-nums max-sm:h-14", shaking && "onb-shake")}
                        data-shaking={shaking ? "true" : "false"}
                      />
                    </Field>
                    <div className="min-h-6 text-sm">
                      {progress}
                      {!changing && calm(error, "onboarding-phone-error", "phone-error")}
                      {calm(notice, "onboarding-phone-notice", "phone-notice")}
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                      <ButtonTip label={waiting ? t("onboardingPage.tips.resendWait", { time: clock(left) }) : phone.codeSent ? t("onboardingPage.tips.resend") : t("onboardingPage.tips.send")} disabled={waiting || sending}>
                      {phone.codeSent || waiting ? (
                        <Button type="button" variant={notice ? "default" : "outline"} disabled={waiting || sending} onClick={() => void send()} data-testid="phone-resend" data-highlight={notice ? "true" : "false"}>
                          {waiting ? t("onboardingPage.phone.resendIn", { time: clock(left) }) : t("onboardingPage.phone.resend")}
                        </Button>
                      ) : (
                        <Button type="button" variant={notice ? "default" : "outline"} disabled={sending} onClick={() => void send()} data-testid="phone-send">
                          {t("onboardingPage.phone.send")}
                        </Button>
                      )}
                      </ButtonTip>
                      <ButtonTip label={t("onboardingPage.tips.changeNumber")}>
                      <Button type="button" variant="link" size="sm" className="text-muted-foreground" onClick={() => setChanging(true)} data-testid="phone-change">
                        {t("onboardingPage.phone.change")}
                      </Button>
                      </ButtonTip>
                    </div>
                  </div>
                </Collapse>
              </Layer>
              {verified && (
                <div style={{ gridArea: "1 / 1" }} className="onb-fade" data-testid="phone-verified-layer">
                  <p className="flex items-center justify-center gap-2 py-2 text-base font-medium" role="status" data-testid="phone-verified">
                    <CheckCircle2 className="h-6 w-6 shrink-0 text-success" aria-hidden="true" />
                    {t("onboardingPage.phone.verified")}
                  </p>
                </div>
              )}
            </div>
          </Enter>
          <p className="sr-only" role="status" aria-live="polite" data-testid="phone-announce">
            {announce}
          </p>
        </div>
      </StepBody>
      <StepFooter onBack={ctx.back} onNext={ctx.next} nextDisabled={!verified} nextReason={t("onboardingPage.tips.verifyFirst")} hint={verified ? null : t("onboardingPage.phone.confirmFirst")} />
    </>
  );
}
